import TableContextProvider, { useTableContext, useTableControls } from '../../../../../../../common/components/table/table-context';
import { Button, Col, Row, Space } from 'antd';
import { SearchFilter } from '../../../../../../../common/components/table/filters/search-filter/search-filter';
import { FilterTypesEnum } from '../../../../../../../common/enums/filter-types.enum';
import { PlusOutlined } from '@ant-design/icons';
import TableBase from '../../../../../../../common/components/table/table-base';
import React from 'react';
import { IResidentAddresses, IResidentDetails } from 'common/services/residents-service/residents.service.types';
import { addressesInitialColumns } from './addresses.config';
import { observer } from 'mobx-react-lite';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { useModal } from '../../../../../../../common/hooks/use-modal/use-modal.hook';
import residentStore from '../../../../../stores/resident.store';
import AddAddressModal from './add-address-modal/add-address-modal';
import { ResidentSourceType } from 'common/enums/resident-source-type.enum';
import ResidentDeleteModal from '../../resident-delete-modal/resident-delete-modal';
import { IDeleteResidentModalProps } from '../../resident-delete-modal/resident-delete-modal.types';
import featureStore from 'common/stores/features/feature.store';

export const Addresses = observer(() => {
  const context = useTableContext();
  const isShowActionButtons =
    !featureStore.isNorthStar || residentStore.selectedResidentAddress?.resident.source !== ResidentSourceType.NorthStar;

  // Table config
  const tableControls = useTableControls({
    clientSide: true,
    data: residentStore.addresses,
    initialColumns: addressesInitialColumns,
  });

  const getDeletePopupMessate = (resident: any) => {
    return (
      <span>
        Are you sure you want to remove{' '}
        <span style={{ fontWeight: 500 }}>
          {resident.firstName} {resident.lastName}
        </span>{' '}
        from this address?{' '}
        <span style={{ fontWeight: 500 }}>
          <br />
          Please note:
        </span>{' '}
        All guests and vehicles associated to this address will also be removed. <br />
        To retain this information,{' '}
        <span style={{ fontWeight: 500 }}>
          please assign a new resident to take ownership of the guests and vehicles before proceeding.
        </span>
      </span>
    );
  };

  const deleteResidentModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const selectedRow: IResidentAddresses = tableControls.getSelectedRow();

      if (!selectedRow.address.id || !residentStore.residentId || !residentStore.ownership) {
        console.error(`Resident is not selected`);
        return;
      }

      const initData: IDeleteResidentModalProps = {
        addressId: selectedRow.address.id,
        residentId: residentStore.residentId,
        ownershipType: residentStore.ownership,
        message: getDeletePopupMessate(residentStore.resident),
      };
      setModalInitData(initData);
    },
    onClose: (isSuccessSaved) => {
      if (isSuccessSaved) {
        const selectedRow: IResidentAddresses = tableControls.getSelectedRow();
        residentStore.deleteAddress(selectedRow.address.id);
        tableControls.setSelectedRow(null);
        context.refetchData();
      }
    },
  });

  const addAddressModal = useModal({
    onBeforeShow: () => {
      const selectedRow: IResidentAddresses = tableControls.getSelectedRow();
      if (!selectedRow) {
        return;
      }
      if (!residentStore.residentId) {
        console.error('Resident must be selected');
        return;
      }
    },
    onClose: (isSuccessSaved, details: IResidentDetails) => {
      if (isSuccessSaved) {
        const addressToAdd = details.addresses.filter(
          (i) => !residentStore.selectedResident?.addresses.map((x) => x.address.id).includes(i.address.id),
        )[0];
        residentStore.addAddress(addressToAdd);
        context.refetchData();
      }
    },
  });

  return (
    <div>
      <TableContextProvider controls={tableControls}>
        <div>
          <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
            <Col flex="auto">
              <SearchFilter
                adaptiveSearch={true}
                enableProgressiveSearch={true}
                placeholder="Search by address"
                rulesForColumn={{
                  'address.street.name': FilterTypesEnum.Contains,
                  'address.streetNumber': FilterTypesEnum.Contains,
                  'address.blockNumber': FilterTypesEnum.Contains,
                  'address.unit': FilterTypesEnum.Contains,
                  'address.lotNumber': FilterTypesEnum.Contains,
                  'address.buildingNumber': FilterTypesEnum.Contains,
                  'address.name': FilterTypesEnum.Contains,
                }}
                rightSide={
                  isShowActionButtons ? (
                    <Space size={8}>
                      <Button
                        icon={<DeleteIcon stroke="#505762" />}
                        size="middle"
                        className="mobile-btn"
                        onClick={deleteResidentModal.showModal}
                        disabled={!tableControls.getSelectedRow()}>
                        Delete
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="middle"
                        className="mobile-btn"
                        disabled={!residentStore.selectedResidentAddress}
                        onClick={addAddressModal?.showModal}>
                        Add
                      </Button>
                    </Space>
                  ) : null
                }
              />
            </Col>
          </Row>
          <TableBase onSelected={tableControls.setSelectedRow} selectFirstRowByDefault />
        </div>
      </TableContextProvider>
      {deleteResidentModal?.isOpenModal && (
        <ResidentDeleteModal
          initData={deleteResidentModal?.initData}
          title={<span style={{ color: '#d1564c' }}>Remove Resident from Address</span>}
          isOpen={deleteResidentModal?.isOpenModal}
          onClose={deleteResidentModal?.hideModal}
        />
      )}
      <AddAddressModal title="Add" isOpen={addAddressModal?.isOpenModal} onClose={addAddressModal?.hideModal} />
    </div>
  );
});
